import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const VERT = '#1B5E20';
const ROUGE = '#C62828';
const ORANGE = '#E65100';
const BLEU = '#1565C0';
const GRIS_BG = '#f7f7f7';

// ─── Icônes SVG ───────────────────────────────────────────────────────
const IcoSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="8" stroke="#666" strokeWidth="2"/>
    <path d="M21 21l-4.35-4.35" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IcoUser = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" stroke="#888" strokeWidth="1.8"/>
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#888" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

// ─── Badge statut connexion ────────────────────────────────────────────
const BadgeStatut = ({ statut }) => {
  const cfg = {
    succes:  { bg: '#E8F5E9', color: '#2E7D32', label: 'Succès' },
    echec:   { bg: '#FFEBEE', color: '#C62828', label: 'Échec' },
    bloque:  { bg: '#FFF3E0', color: '#E65100', label: 'Bloqué' },
  };
  const c = cfg[statut] || { bg: '#F5F5F5', color: '#666', label: statut };
  return (
    <span style={{ background: c.bg, color: c.color, borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {c.label}
    </span>
  );
};

// ─── Badge action audit ────────────────────────────────────────────────
const BadgeAction = ({ action }) => {
  const cfg = {
    INSERT: { bg: '#E8F5E9', color: '#2E7D32' },
    UPDATE: { bg: '#E3F2FD', color: '#1565C0' },
    DELETE: { bg: '#FFEBEE', color: '#C62828' },
  };
  const key = (action || '').toUpperCase();
  const c = cfg[key] || { bg: '#F5F5F5', color: '#666' };
  return (
    <span style={{ background: c.bg, color: c.color, borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
      {action}
    </span>
  );
};

// ─── Avatar initiales ─────────────────────────────────────────────────
const Avatar = ({ nom, prenom, size = 30 }) => {
  const init = [(prenom || '')[0], (nom || '')[0]].filter(Boolean).join('').toUpperCase() || '?';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: '#C8E6C9', color: VERT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, flexShrink: 0,
    }}>
      {init}
    </div>
  );
};

// ─── Stat card ────────────────────────────────────────────────────────
const StatCard = ({ label, value, color, subtitle }) => (
  <div style={{
    background: '#fff', borderRadius: 10, padding: '16px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    borderLeft: `4px solid ${color}`,
    flex: 1, minWidth: 160,
  }}>
    <div style={{ fontSize: 28, fontWeight: 800, color }}>{value ?? '—'}</div>
    <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{label}</div>
    {subtitle && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{subtitle}</div>}
  </div>
);

// ─── Formatage heure ──────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtJSON = (obj) => {
  if (!obj) return '—';
  try {
    const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
    const keys = Object.keys(parsed).slice(0, 3);
    return keys.map(k => `${k}: ${String(parsed[k]).substring(0, 20)}`).join(', ');
  } catch { return String(obj).substring(0, 40); }
};

// ─── Ligne couleur selon statut ───────────────────────────────────────
const rowBg = (statut, motif) => {
  if (statut === 'succes') return '#F1F8E9';
  if (statut === 'bloque') return '#FFF3E0';
  if (statut === 'echec') {
    if (motif && motif.includes('tentative 3')) return '#FFF8E1';
    return '#FFEBEE';
  }
  return '#fff';
};

const inputStyle = {
  padding: '7px 10px', border: '1px solid #E0E0E0',
  borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff',
};

// ─── Page principale ──────────────────────────────────────────────────
export default function AuditPage() {
  const [onglet, setOnglet] = useState('connexions');
  const [stats, setStats] = useState(null);
  const [connexions, setConnexions] = useState([]);
  const [actions, setActions] = useState([]);
  const [totalConnexions, setTotalConnexions] = useState(0);
  const [totalActions, setTotalActions] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filtres connexions
  const [filtreDate, setFiltreDate] = useState(new Date().toISOString().split('T')[0]);
  const [filtreStatut, setFiltreStatut] = useState('');
  const [filtreRecherche, setFiltreRecherche] = useState('');
  const [pageConnexions, setPageConnexions] = useState(0);

  // Filtres actions
  const [filtreDateA, setFiltreDateA] = useState('');
  const [filtreModule, setFiltreModule] = useState('');
  const [filtreAction, setFiltreAction] = useState('');
  const [pageActions, setPageActions] = useState(0);

  const LIMIT = 50;

  const chargerStats = useCallback(async () => {
    try {
      const r = await axios.get('/api/audit/statistiques');
      setStats(r.data);
    } catch (e) { console.error(e); }
  }, []);

  const chargerConnexions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, offset: pageConnexions * LIMIT };
      if (filtreDate)    { params.date_debut = filtreDate; params.date_fin = filtreDate; }
      if (filtreStatut)  params.statut = filtreStatut;
      const r = await axios.get('/api/audit/connexions', { params });
      let rows = r.data.connexions;
      if (filtreRecherche) {
        const q = filtreRecherche.toLowerCase();
        rows = rows.filter(c =>
          `${c.prenom} ${c.nom}`.toLowerCase().includes(q) ||
          (c.matricule || '').toLowerCase().includes(q)
        );
      }
      setConnexions(rows);
      setTotalConnexions(r.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filtreDate, filtreStatut, filtreRecherche, pageConnexions]);

  const chargerActions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, offset: pageActions * LIMIT };
      if (filtreDateA)   { params.date_debut = filtreDateA; params.date_fin = filtreDateA; }
      if (filtreModule)  params.module = filtreModule;
      if (filtreAction)  params.action = filtreAction;
      const r = await axios.get('/api/audit/actions', { params });
      setActions(r.data.actions);
      setTotalActions(r.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filtreDateA, filtreModule, filtreAction, pageActions]);

  useEffect(() => { chargerStats(); }, [chargerStats]);
  useEffect(() => { if (onglet === 'connexions') chargerConnexions(); }, [onglet, chargerConnexions]);
  useEffect(() => { if (onglet === 'actions') chargerActions(); }, [onglet, chargerActions]);

  const tabStyle = (actif) => ({
    padding: '8px 20px', border: 'none', cursor: 'pointer',
    background: actif ? VERT : 'transparent',
    color: actif ? '#fff' : '#555',
    fontWeight: actif ? 700 : 500,
    fontSize: 13, borderRadius: 6,
    fontFamily: 'inherit',
    transition: 'background 0.15s',
  });

  return (
    <Layout>
      <div style={{ background: GRIS_BG, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

        {/* ── Header ── */}
        <div className="sg-bandeau-clair" style={{ background: VERT, padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="#fff" strokeWidth="2"/>
            <path d="M21 21l-4.35-4.35" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Journal d'activité</span>
          <div style={{ flex: 1 }} />
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>

        <div style={{ padding: '16px 24px', maxWidth: 1400, margin: '0 auto' }}>

          {/* ── 4 cartes statistiques ── */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <StatCard label="Connexions aujourd'hui" value={stats?.connexions_aujourd_hui} color={VERT} />
            <StatCard label="Échecs aujourd'hui" value={stats?.echecs_aujourd_hui} color={ORANGE} />
            <StatCard label="Comptes bloqués" value={stats?.comptes_bloques} color={ROUGE} />
            <StatCard label="Actions enregistrées" value={stats?.total_actions} color={BLEU} />
          </div>

          {/* ── Onglets ── */}
          <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #eee', display: 'flex', gap: 8, alignItems: 'center' }}>
              <button style={tabStyle(onglet === 'connexions')} onClick={() => setOnglet('connexions')}>
                Connexions
              </button>
              <button style={tabStyle(onglet === 'actions')} onClick={() => setOnglet('actions')}>
                Actions
              </button>
            </div>

            {/* ══ ONGLET CONNEXIONS ══ */}
            {onglet === 'connexions' && (
              <div>
                {/* Filtres */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="date"
                    value={filtreDate}
                    onChange={e => { setFiltreDate(e.target.value); setPageConnexions(0); }}
                    style={inputStyle}
                  />
                  <select
                    value={filtreStatut}
                    onChange={e => { setFiltreStatut(e.target.value); setPageConnexions(0); }}
                    style={inputStyle}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="succes">Succès</option>
                    <option value="echec">Échec</option>
                    <option value="bloque">Bloqué</option>
                  </select>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <div style={{ position: 'absolute', left: 8 }}><IcoSearch /></div>
                    <input
                      type="text"
                      placeholder="Nom ou matricule..."
                      value={filtreRecherche}
                      onChange={e => setFiltreRecherche(e.target.value)}
                      style={{ ...inputStyle, paddingLeft: 28, width: 200 }}
                    />
                  </div>
                  <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
                    {totalConnexions} entrée{totalConnexions > 1 ? 's' : ''}
                  </span>
                </div>

                {/* Tableau */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#F5F5F5', textAlign: 'left' }}>
                        {['Employé', 'Matricule', 'Rôle', 'Heure', 'IP', 'Statut', 'Motif'].map(h => (
                          <th key={h} style={{ padding: '9px 14px', color: '#555', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', borderBottom: '1px solid #E0E0E0' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Chargement...</td></tr>
                      ) : connexions.length === 0 ? (
                        <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Aucune connexion trouvée</td></tr>
                      ) : connexions.map(c => (
                        <tr key={c.id} style={{ background: rowBg(c.statut, c.motif), borderBottom: '1px solid #F0F0F0' }}>
                          <td style={{ padding: '9px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Avatar nom={c.nom} prenom={c.prenom} size={28} />
                              <span style={{ fontWeight: 500 }}>{c.prenom} {c.nom}</span>
                            </div>
                          </td>
                          <td style={{ padding: '9px 14px', color: '#555' }}>{c.matricule || '—'}</td>
                          <td style={{ padding: '9px 14px', color: '#555', fontSize: 12 }}>{c.role || '—'}</td>
                          <td style={{ padding: '9px 14px', color: '#555', whiteSpace: 'nowrap' }}>{fmtDate(c.created_at)}</td>
                          <td style={{ padding: '9px 14px', color: '#666', fontFamily: 'monospace', fontSize: 12 }}>{c.ip_adresse || '—'}</td>
                          <td style={{ padding: '9px 14px' }}><BadgeStatut statut={c.statut} /></td>
                          <td style={{ padding: '9px 14px', color: '#666', fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.motif || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalConnexions > LIMIT && (
                  <div style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid #eee' }}>
                    <button disabled={pageConnexions === 0}
                      onClick={() => setPageConnexions(p => p - 1)}
                      style={{ ...inputStyle, cursor: pageConnexions === 0 ? 'default' : 'pointer', opacity: pageConnexions === 0 ? 0.5 : 1 }}>
                      ← Précédent
                    </button>
                    <span style={{ fontSize: 12, color: '#666' }}>
                      Page {pageConnexions + 1} / {Math.ceil(totalConnexions / LIMIT)}
                    </span>
                    <button disabled={pageConnexions >= Math.ceil(totalConnexions / LIMIT) - 1}
                      onClick={() => setPageConnexions(p => p + 1)}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                      Suivant →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ══ ONGLET ACTIONS ══ */}
            {onglet === 'actions' && (
              <div>
                {/* Filtres */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="date"
                    value={filtreDateA}
                    onChange={e => { setFiltreDateA(e.target.value); setPageActions(0); }}
                    style={inputStyle}
                    placeholder="Date"
                  />
                  <input
                    type="text"
                    placeholder="Module (table)..."
                    value={filtreModule}
                    onChange={e => { setFiltreModule(e.target.value); setPageActions(0); }}
                    style={{ ...inputStyle, width: 160 }}
                  />
                  <select
                    value={filtreAction}
                    onChange={e => { setFiltreAction(e.target.value); setPageActions(0); }}
                    style={inputStyle}
                  >
                    <option value="">Toutes les actions</option>
                    <option value="INSERT">INSERT</option>
                    <option value="UPDATE">UPDATE</option>
                    <option value="DELETE">DELETE</option>
                  </select>
                  <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
                    {totalActions} entrée{totalActions > 1 ? 's' : ''}
                  </span>
                </div>

                {/* Tableau */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#F5F5F5', textAlign: 'left' }}>
                        {['Auteur', 'Action', 'Module', 'Employé ciblé', 'Avant → Après', 'Heure'].map(h => (
                          <th key={h} style={{ padding: '9px 14px', color: '#555', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', borderBottom: '1px solid #E0E0E0' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Chargement...</td></tr>
                      ) : actions.length === 0 ? (
                        <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Aucune action enregistrée</td></tr>
                      ) : actions.map(a => (
                        <tr key={a.id} style={{ borderBottom: '1px solid #F0F0F0', background: '#fff' }}>
                          <td style={{ padding: '9px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Avatar nom={a.acteur_nom} prenom={a.acteur_prenom} size={28} />
                              <div>
                                <div style={{ fontWeight: 500 }}>{a.acteur_prenom} {a.acteur_nom}</div>
                                <div style={{ fontSize: 11, color: '#888' }}>{a.acteur_role || '—'}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '9px 14px' }}><BadgeAction action={a.action} /></td>
                          <td style={{ padding: '9px 14px', color: '#555', fontFamily: 'monospace', fontSize: 12 }}>{a.module || '—'}</td>
                          <td style={{ padding: '9px 14px' }}>
                            {a.cible_nom ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <IcoUser />
                                <span style={{ fontSize: 12 }}>{a.cible_prenom} {a.cible_nom}</span>
                              </div>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '9px 14px', maxWidth: 240 }}>
                            <div style={{ fontSize: 11, color: '#555', overflow: 'hidden' }}>
                              {a.donnees_avant && (
                                <div style={{ color: ROUGE, marginBottom: 2 }}>
                                  <span style={{ fontWeight: 600 }}>Avant : </span>{fmtJSON(a.donnees_avant)}
                                </div>
                              )}
                              {a.donnees_apres && (
                                <div style={{ color: VERT }}>
                                  <span style={{ fontWeight: 600 }}>Après : </span>{fmtJSON(a.donnees_apres)}
                                </div>
                              )}
                              {!a.donnees_avant && !a.donnees_apres && '—'}
                            </div>
                          </td>
                          <td style={{ padding: '9px 14px', color: '#555', whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalActions > LIMIT && (
                  <div style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid #eee' }}>
                    <button disabled={pageActions === 0}
                      onClick={() => setPageActions(p => p - 1)}
                      style={{ ...inputStyle, cursor: pageActions === 0 ? 'default' : 'pointer', opacity: pageActions === 0 ? 0.5 : 1 }}>
                      ← Précédent
                    </button>
                    <span style={{ fontSize: 12, color: '#666' }}>
                      Page {pageActions + 1} / {Math.ceil(totalActions / LIMIT)}
                    </span>
                    <button disabled={pageActions >= Math.ceil(totalActions / LIMIT) - 1}
                      onClick={() => setPageActions(p => p + 1)}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                      Suivant →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Top 5 utilisateurs ── */}
          {stats?.top_utilisateurs?.length > 0 && (
            <div style={{ marginTop: 16, background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '14px 18px' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#333' }}>Top 5 utilisateurs actifs</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {stats.top_utilisateurs.map((u, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: '#F9FBE7', borderRadius: 8, padding: '6px 12px',
                  }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: VERT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>
                      {i + 1}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{u.prenom} {u.nom}</span>
                    <span style={{ fontSize: 11, color: '#888' }}>({u.nb_connexions} conn.)</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}
